import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import VampStandingPill from "./VampStandingPill";
import { fmtInt, reading, sumTotals, type VampMonthContext } from "./vampStanding";
import type { VampMonthTotals } from "../../../types/vampRates";

dayjs.extend(utc);

interface VampMonthHistoryProps {
  history: VampMonthTotals[];
  ctx: VampMonthContext;
  isCaid: boolean;
  unmatched: boolean;
}

// Each min-w matches its own row's fixed columns plus the Transactions column's 60px floor, its
// gaps and its padding, so every row - header included - shares one guaranteed-identical width.
const GRID_CAID = "grid-cols-[92px_minmax(60px,1fr)_72px_62px_86px_72px_124px] min-w-[672px]";
const GRID_ACCOUNT = "grid-cols-[92px_minmax(60px,1fr)_72px_62px_86px_72px] min-w-[538px]";

// The months before an account started say nothing, so the table begins at the oldest month with
// data - a quiet month between two active ones is a real reading and stays. With no data anywhere
// the month being viewed still shows.
function shownMonths(history: VampMonthTotals[]): VampMonthTotals[] {
  const oldestFirst = [...history].sort((a, b) => a.monthStart.localeCompare(b.monthStart));
  const first = oldestFirst.findIndex((m) => m.settled + m.tc40 + m.tc15 > 0);
  return oldestFirst.slice(first === -1 ? oldestFirst.length - 1 : first).reverse();
}

export default function VampMonthHistory({ history, ctx, isCaid, unmatched }: VampMonthHistoryProps) {
  const grid = `grid ${isCaid ? GRID_CAID : GRID_ACCOUNT} items-center gap-x-2.5 overflow-hidden px-[22px]`;
  const rows = shownMonths(history);

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-[22px] pb-2.5 pt-3">
        <span className="text-sm font-semibold text-gray-900">Last 6 months</span>
      </div>
      <div className="overflow-x-auto">
        <div className={`${grid} bg-gray-50 py-2 text-[11px] font-medium uppercase leading-4 tracking-[0.04em] text-gray-400`}>
          <span>Month</span>
          <span className="text-right">Transactions</span>
          <span className="text-right">TC40</span>
          <span className="text-right">TC15</span>
          <span className="text-right">Events</span>
          <span className="text-right">Rate</span>
          {isCaid && <span className="text-right">Status</span>}
        </div>
        {rows.map((m, i) => {
          // Only the requested month is partly elapsed; every earlier month is complete.
          const r = reading(sumTotals([m]), m.monthStart.startsWith(ctx.month) ? ctx.elapsed : 1, unmatched);
          // An account row shows no per-CAID scoring, so its events never read as scored.
          const scored = isCaid && r.scored;
          return (
            <div key={m.monthStart} className={`${grid} border-t border-gray-100 py-[9px] ${i === 0 ? "bg-gray-25" : ""}`}>
              <span className="text-[13px] font-semibold leading-5 text-gray-700">{dayjs.utc(m.monthStart).format("MMM YYYY")}</span>
              <span className="text-right text-[13px] leading-5 text-gray-600 tabular-nums">{fmtInt(m.settled)}</span>
              <span className="text-right text-[13px] leading-5 text-gray-600 tabular-nums">{fmtInt(m.tc40)}</span>
              <span className="text-right text-[13px] leading-5 text-gray-600 tabular-nums">{fmtInt(m.tc15)}</span>
              <span className={`text-right text-[13px] leading-5 tabular-nums ${scored ? "font-bold text-gray-900" : "text-gray-400"}`}>{fmtInt(r.totals.events)}</span>
              <span className={`text-right text-[13px] font-bold leading-5 tabular-nums ${isCaid ? r.rateInk : "text-gray-900"}`}>
                {r.rateLabel}
              </span>
              {isCaid && (
                <span className="justify-self-end">
                  <VampStandingPill standing={r.standing} />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
